import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import db, { logSyncAction } from '../services/database';

const AnnotationContext = createContext(null);

export function AnnotationProvider({ children }) {
  const [annotations, setAnnotations] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [earmarks, setEarmarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Load all annotations on mount
  useEffect(() => {
    async function loadAnnotations() {
      try {
        const [annotationsData, highlightsData, notesData, bookmarksData, earmarksData] = await Promise.all([
          db.annotations.toArray(),
          db.highlights.toArray(),
          db.notes.toArray(),
          db.bookmarks.toArray(),
          db.earmarks.toArray()
        ]);
        
        setAnnotations(annotationsData);
        setHighlights(highlightsData);
        setNotes(notesData);
        setBookmarks(bookmarksData);
        setEarmarks(earmarksData);
      } catch (err) {
        console.error('Failed to load annotations:', err);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadAnnotations();
  }, []);
  
  // Highlight colors
  const highlightColors = {
    yellow: 'rgba(218, 165, 32, 0.3)',
    green: 'rgba(107, 142, 35, 0.3)',
    blue: 'rgba(100, 149, 237, 0.3)',
    pink: 'rgba(219, 112, 147, 0.3)',
    purple: 'rgba(147, 112, 219, 0.3)'
  };
  
  // Add highlight
  const addHighlight = useCallback(async (verseId, bibleId, color = 'yellow', text = '') => {
    const highlight = {
      verseId,
      bibleId,
      color,
      text,
      createdAt: new Date().toISOString()
    };
    
    const id = await db.highlights.add(highlight);
    const newHighlight = { ...highlight, id };
    setHighlights(prev => [...prev, newHighlight]);
    await logSyncAction('create', 'highlight', id);
    
    return newHighlight;
  }, []);
  
  // Remove highlight
  const removeHighlight = useCallback(async (id) => {
    await db.highlights.delete(id);
    setHighlights(prev => prev.filter(h => h.id !== id));
    await logSyncAction('delete', 'highlight', id);
  }, []);
  
  // Update highlight color
  const updateHighlightColor = useCallback(async (id, color) => {
    await db.highlights.update(id, { color });
    setHighlights(prev => prev.map(h => h.id === id ? { ...h, color } : h));
    await logSyncAction('update', 'highlight', id);
  }, []);
  
  // Get highlights for a verse
  const getHighlightsForVerse = useCallback((verseId, bibleId) => {
    return highlights.filter(h => h.verseId === verseId && h.bibleId === bibleId);
  }, [highlights]);
  
  // Get highlights for a chapter
  const getHighlightsForChapter = useCallback((chapterId, bibleId) => {
    return highlights.filter(h => 
      h.verseId.startsWith(chapterId) && h.bibleId === bibleId
    );
  }, [highlights]);
  
  // Add note
  const addNote = useCallback(async (verseId, bibleId, content, tags = []) => {
    const note = {
      verseId,
      bibleId,
      content,
      tags,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const id = await db.notes.add(note);
    const newNote = { ...note, id };
    setNotes(prev => [...prev, newNote]);
    await logSyncAction('create', 'note', id);
    
    return newNote;
  }, []);
  
  // Update note
  const updateNote = useCallback(async (id, content, tags) => {
    const updates = {
      content,
      tags,
      updatedAt: new Date().toISOString()
    };
    
    await db.notes.update(id, updates);
    setNotes(prev => prev.map(n => n.id === id ? { ...n, ...updates } : n));
    await logSyncAction('update', 'note', id);
  }, []);
  
  // Delete note
  const deleteNote = useCallback(async (id) => {
    await db.notes.delete(id);
    setNotes(prev => prev.filter(n => n.id !== id));
    await logSyncAction('delete', 'note', id);
  }, []);
  
  // Get notes for a verse
  const getNotesForVerse = useCallback((verseId, bibleId) => {
    return notes.filter(n => n.verseId === verseId && n.bibleId === bibleId);
  }, [notes]);
  
  // Get notes for a chapter
  const getNotesForChapter = useCallback((chapterId, bibleId) => {
    return notes.filter(n => 
      n.verseId.startsWith(chapterId) && n.bibleId === bibleId
    );
  }, [notes]);
  
  // Search notes
  const searchNotes = useCallback((query) => {
    const lowerQuery = query.toLowerCase();
    return notes.filter(n => 
      n.content.toLowerCase().includes(lowerQuery) ||
      n.tags.some(t => t.toLowerCase().includes(lowerQuery))
    );
  }, [notes]);
  
  // Add bookmark
  const addBookmark = useCallback(async (verseId, bibleId, folder = 'default', label = '') => {
    const bookmark = {
      verseId,
      bibleId,
      folder,
      label,
      createdAt: new Date().toISOString()
    };
    
    const id = await db.bookmarks.add(bookmark);
    const newBookmark = { ...bookmark, id };
    setBookmarks(prev => [...prev, newBookmark]);
    await logSyncAction('create', 'bookmark', id);
    
    return newBookmark;
  }, []);
  
  // Remove bookmark
  const removeBookmark = useCallback(async (id) => {
    await db.bookmarks.delete(id);
    setBookmarks(prev => prev.filter(b => b.id !== id));
    await logSyncAction('delete', 'bookmark', id);
  }, []);
  
  // Update bookmark
  const updateBookmark = useCallback(async (id, updates) => {
    await db.bookmarks.update(id, updates);
    setBookmarks(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
    await logSyncAction('update', 'bookmark', id);
  }, []);
  
  // Check if verse is bookmarked
  const isBookmarked = useCallback((verseId, bibleId) => {
    return bookmarks.some(b => b.verseId === verseId && b.bibleId === bibleId);
  }, [bookmarks]);
  
  // Get bookmarks by folder
  const getBookmarksByFolder = useCallback((folder) => {
    return bookmarks.filter(b => b.folder === folder);
  }, [bookmarks]);
  
  // Get all bookmark folders
  const getBookmarkFolders = useCallback(() => {
    const folders = new Set(bookmarks.map(b => b.folder));
    return Array.from(folders);
  }, [bookmarks]);
  
  // Add earmark (chapter-level bookmark)
  const addEarmark = useCallback(async (chapterId, bibleId, bookName, chapterNum) => {
    const earmark = {
      chapterId,
      bibleId,
      bookName,
      chapterNum,
      createdAt: new Date().toISOString()
    };
    
    const id = await db.earmarks.add(earmark);
    const newEarmark = { ...earmark, id };
    setEarmarks(prev => [...prev, newEarmark]);
    await logSyncAction('create', 'earmark', id);
    
    return newEarmark;
  }, []);
  
  // Remove earmark
  const removeEarmark = useCallback(async (id) => {
    await db.earmarks.delete(id);
    setEarmarks(prev => prev.filter(e => e.id !== id));
    await logSyncAction('delete', 'earmark', id);
  }, []);
  
  // Check if chapter is earmarked
  const isEarmarked = useCallback((chapterId, bibleId) => {
    return earmarks.some(e => e.chapterId === chapterId && e.bibleId === bibleId);
  }, [earmarks]);
  
  // Get earmark for chapter
  const getEarmark = useCallback((chapterId, bibleId) => {
    return earmarks.find(e => e.chapterId === chapterId && e.bibleId === bibleId);
  }, [earmarks]);
  
  // Toggle earmark
  const toggleEarmark = useCallback(async (chapterId, bibleId, bookName, chapterNum) => {
    const existing = getEarmark(chapterId, bibleId);
    if (existing) {
      await removeEarmark(existing.id);
      return null;
    } else {
      return await addEarmark(chapterId, bibleId, bookName, chapterNum);
    }
  }, [getEarmark, removeEarmark, addEarmark]);

  // Add annotation (generic - can be note, sketch, voice memo)
  const addAnnotation = useCallback(async (verseId, bibleId, type, data, tags = []) => {
    const annotation = {
      verseId,
      bibleId,
      type, // 'text', 'sketch', 'voice'
      data,
      tags,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const id = await db.annotations.add(annotation);
    const newAnnotation = { ...annotation, id };
    setAnnotations(prev => [...prev, newAnnotation]);
    await logSyncAction('create', 'annotation', id);
    
    return newAnnotation;
  }, []);
  
  // Update annotation
  const updateAnnotation = useCallback(async (id, data, tags) => {
    const updates = {
      data,
      tags,
      updatedAt: new Date().toISOString()
    };
    
    await db.annotations.update(id, updates);
    setAnnotations(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    await logSyncAction('update', 'annotation', id);
  }, []);
  
  // Delete annotation
  const deleteAnnotation = useCallback(async (id) => {
    await db.annotations.delete(id);
    setAnnotations(prev => prev.filter(a => a.id !== id));
    await logSyncAction('delete', 'annotation', id);
  }, []);
  
  // Get all annotations for a verse
  const getAnnotationsForVerse = useCallback((verseId, bibleId) => {
    return {
      highlights: getHighlightsForVerse(verseId, bibleId),
      notes: getNotesForVerse(verseId, bibleId),
      annotations: annotations.filter(a => a.verseId === verseId && a.bibleId === bibleId)
    };
  }, [annotations, getHighlightsForVerse, getNotesForVerse]);
  
  // Get all tags used
  const getAllTags = useCallback(() => {
    const tagSet = new Set();
    
    notes.forEach(n => n.tags?.forEach(t => tagSet.add(t)));
    annotations.forEach(a => a.tags?.forEach(t => tagSet.add(t)));
    
    return Array.from(tagSet).sort();
  }, [notes, annotations]);
  
  const value = {
    isLoading,
    
    // Highlights
    highlights,
    highlightColors,
    addHighlight,
    removeHighlight,
    updateHighlightColor,
    getHighlightsForVerse,
    getHighlightsForChapter,
    
    // Notes
    notes,
    addNote,
    updateNote,
    deleteNote,
    getNotesForVerse,
    getNotesForChapter,
    searchNotes,
    
    // Bookmarks
    bookmarks,
    addBookmark,
    removeBookmark,
    updateBookmark,
    isBookmarked,
    getBookmarksByFolder,
    getBookmarkFolders,
    
    // Earmarks (chapter-level)
    earmarks,
    addEarmark,
    removeEarmark,
    isEarmarked,
    getEarmark,
    toggleEarmark,
    
    // Generic annotations
    annotations,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    getAnnotationsForVerse,
    
    // Utilities
    getAllTags
  };
  
  return (
    <AnnotationContext.Provider value={value}>
      {children}
    </AnnotationContext.Provider>
  );
}

export function useAnnotations() {
  const context = useContext(AnnotationContext);
  if (!context) {
    throw new Error('useAnnotations must be used within an AnnotationProvider');
  }
  return context;
}

export default AnnotationContext;
